// Builds "Add to Calendar" data (.ics download + Google Calendar link) for a
// Dashboard hearing — used by TodaysHearings in Dashboard.js — and for upcoming
// rulemaking hearings and comment deadlines (UpcomingRulemaking).

const LOWERCASE_WORDS = new Set([
  'a', 'an', 'the',
  'and', 'but', 'or', 'nor', 'for', 'yet', 'so',
  'at', 'by', 'in', 'of', 'on', 'to', 'up', 'as', 'if',
]);

function toTitleCase(str) {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map((word, i, arr) => {
      if (!word) return word;
      if (i === 0 || i === arr.length - 1 || !LOWERCASE_WORDS.has(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
}

// Matches the "9 a.m." / "9:30 a.m." / "09:00:00" / "9:00 am" formats used by
// leginfo/budget agenda_time strings (see routes/api/dashboard.js's timeVal
// and Dashboard.js's formatTime). Returns null for unparseable strings like
// "Upon Adjournment" or "Upon Call of the Chair".
function parseHearingTime(timeStr) {
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2})(?::(\d{2}))?/);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  const lower = timeStr.toLowerCase();
  if ((lower.includes('p.m.') || lower.includes('pm')) && h < 12) h += 12;
  if ((lower.includes('a.m.') || lower.includes('am')) && h === 12) h = 0;
  if (h > 23 || m > 59) return null;
  return { hours: h, minutes: m };
}

// California hearings are always Pacific time. Since JS has no built-in
// Pacific->UTC conversion without a timezone library, apply the US DST rule
// directly (2nd Sunday in March through 1st Sunday in November) — hearings
// are always scheduled during business hours, never inside the 1am-3am
// transition window, so day-level granularity is sufficient.
function isPacificDST(year, month, day) {
  const nthSunday = (y, m, n) => {
    const d = new Date(Date.UTC(y, m - 1, 1));
    const firstSunday = 1 + ((7 - d.getUTCDay()) % 7);
    return firstSunday + (n - 1) * 7;
  };
  const dayNum = Date.UTC(year, month - 1, day);
  const start = Date.UTC(year, 2, nthSunday(year, 3, 2));
  const end = Date.UTC(year, 10, nthSunday(year, 11, 1));
  return dayNum >= start && dayNum < end;
}

function pacificToUTCDate(year, month, day, hours, minutes) {
  const offsetHours = isPacificDST(year, month, day) ? 7 : 8;
  return new Date(Date.UTC(year, month - 1, day, hours + offsetHours, minutes));
}

function isTracked(item, trackedBills, teamTrackedBills) {
  return !!item.bill_id && (trackedBills?.has(item.bill_id) || teamTrackedBills?.has(item.bill_id));
}

function formatAgendaItem(item) {
  const code = item.item_code || '';
  const desc = item.description ? ` — ${item.description}` : '';
  const url = item.url ? ` ${item.url}` : '';
  return `${code}${desc}${url}`.trim();
}

function buildAgendaDescription(hearing, trackedBills, teamTrackedBills) {
  const items = hearing.items || [];
  const tracked = items.filter((item) => isTracked(item, trackedBills, teamTrackedBills));
  const lines = [];

  if (hearing.agency) lines.push(hearing.agency, '');

  if (tracked.length > 0) {
    lines.push("Bills You're Tracking:");
    tracked.forEach((item) => lines.push(`- ${formatAgendaItem(item)}`));
    lines.push('');
  }

  lines.push('Full Agenda:');
  if (items.length > 0) {
    items.forEach((item) => lines.push(`- ${formatAgendaItem(item)}`));
  } else {
    lines.push('(No agenda items listed)');
  }

  if (hearing.chair) lines.push('', `Chair: ${hearing.chair}`);

  return lines.join('\n');
}

// Builds a plain event descriptor consumed by both buildICS and googleCalendarUrl.
export function buildHearingEvent(hearing, hearingDate, trackedBills, teamTrackedBills) {
  const year = hearingDate.getFullYear();
  const month = hearingDate.getMonth() + 1;
  const day = hearingDate.getDate();

  const title = (hearing.category === 'agency' ? hearing.name : toTitleCase(hearing.name)) || 'Committee Hearing';
  const location = hearing.location || hearing.loc_line_1 || '';
  const description = buildAgendaDescription(hearing, trackedBills, teamTrackedBills);

  const time = parseHearingTime(hearing.agenda_time);
  if (!time) {
    return { title, location, description, allDay: true, dateOnly: { year, month, day } };
  }

  const start = pacificToUTCDate(year, month, day, time.hours, time.minutes);
  const end = new Date(start.getTime() + 60 * 60 * 1000); // default 1-hour duration
  return { title, location, description, allDay: false, start, end };
}

// Calendar event for a rulemaking milestone. `date` is a YYYY-MM-DD string and `timeStr` an optional
// "10:00 a.m." style time; with no parseable time the event is all-day (comment deadlines always are).
export function buildRulemakingEvent({ title, location, description, date, timeStr }) {
  const [year, month, day] = date.split('-').map(Number);
  const time = parseHearingTime(timeStr);
  if (!time) {
    return { title, location: location || '', description, allDay: true, dateOnly: { year, month, day } };
  }
  const start = pacificToUTCDate(year, month, day, time.hours, time.minutes);
  return { title, location: location || '', description, allDay: false, start, end: new Date(start.getTime() + 60 * 60 * 1000) };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function toICSDateUTC(date) {
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}00Z`;
}

function toICSDateOnly(year, month, day) {
  return `${year}${pad(month)}${pad(day)}`;
}

function nextCalendarDay(year, month, day) {
  const d = new Date(Date.UTC(year, month - 1, day + 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function escapeICSText(str) {
  return String(str || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

// RFC 5545 line folding — no line may exceed 75 octets.
function foldICSLine(line) {
  if (line.length <= 75) return line;
  let result = '';
  let remaining = line;
  while (remaining.length > 75) {
    result += remaining.slice(0, 75) + '\r\n ';
    remaining = remaining.slice(75);
  }
  return result + remaining;
}

export function buildICS(event) {
  const uid = `hearing-${Date.now()}-${Math.random().toString(36).slice(2)}@ellogovna.com`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//elloGovna//Dashboard Hearings//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toICSDateUTC(new Date())}`,
  ];

  if (event.allDay) {
    const { year, month, day } = event.dateOnly;
    const next = nextCalendarDay(year, month, day);
    lines.push(
      `DTSTART;VALUE=DATE:${toICSDateOnly(year, month, day)}`,
      `DTEND;VALUE=DATE:${toICSDateOnly(next.year, next.month, next.day)}`,
    );
  } else {
    lines.push(`DTSTART:${toICSDateUTC(event.start)}`, `DTEND:${toICSDateUTC(event.end)}`);
  }

  lines.push(`SUMMARY:${escapeICSText(event.title)}`);
  if (event.location) lines.push(`LOCATION:${escapeICSText(event.location)}`);
  lines.push(`DESCRIPTION:${escapeICSText(event.description)}`, 'END:VEVENT', 'END:VCALENDAR');

  return lines.map(foldICSLine).join('\r\n') + '\r\n';
}

export function downloadICS(event, filename) {
  const ics = buildICS(event);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.ics') ? filename : `${filename}.ics`;
  link.click();
  URL.revokeObjectURL(url);
}

// Google's calendar/render endpoint returns 400 once the request URL gets
// too long — observed failing around ~16,000 characters (full committee/floor
// agendas with dozens of bills easily exceed this). Stay well under that.
const GOOGLE_URL_SAFE_LIMIT = 7500;
const TRUNCATION_NOTE = '\n\n(Agenda truncated for Google Calendar — see the full agenda in elloGovna.)';

export function googleCalendarUrl(event) {
  const build = (details) => {
    const params = new URLSearchParams();
    params.set('action', 'TEMPLATE');
    params.set('text', event.title);

    if (event.allDay) {
      const { year, month, day } = event.dateOnly;
      const next = nextCalendarDay(year, month, day);
      params.set('dates', `${toICSDateOnly(year, month, day)}/${toICSDateOnly(next.year, next.month, next.day)}`);
    } else {
      params.set('dates', `${toICSDateUTC(event.start)}/${toICSDateUTC(event.end)}`);
    }

    params.set('details', details);
    if (event.location) params.set('location', event.location);
    params.set('ctz', 'America/Los_Angeles');

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  let details = event.description;
  let url = build(details);
  let guard = 0;
  // URL-encoding only ever expands characters, so trimming at least as many
  // raw characters as the overage guarantees convergence.
  while (url.length > GOOGLE_URL_SAFE_LIMIT && details.length > TRUNCATION_NOTE.length && guard < 50) {
    const excess = url.length - GOOGLE_URL_SAFE_LIMIT;
    details = details.slice(0, Math.max(0, details.length - excess - TRUNCATION_NOTE.length)) + TRUNCATION_NOTE;
    url = build(details);
    guard++;
  }

  return url;
}
